import React, { useMemo } from "react";
import { Alert, Box, Button, Divider, Grid, Paper, Stack, Typography } from "@mui/material";
import { SelectElement, TextFieldElement, CheckboxElement, useForm } from "react-hook-form-mui";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useErrorLogging } from "../hooks/useErrorLogging";
import { API, graphqlOperation } from "aws-amplify";
import { getLLMUsageTier } from "../graphql/queries";
import { GetLLMUsageTierQuery } from "../API";
import { GraphQLResult } from "@aws-amplify/api";
import { updateLLMUsageTierAfterEdit } from "../dataaccess/updateLLMUsageTierAfterEdit";
import { createLLMUsageTierAfterEdit } from "../dataaccess/createLLMUsageTierAfterEdit";
import { isUsageTierInUse } from "../dataaccess/isUsageTierInUse";
import { cloneLLMUsageTier } from "../dataaccess/cloneLLMUsageTier";
import { activateLLMUsageTier } from "../dataaccess/activateLLMUsageTier";
import { deactivateLLMUsageTier } from "../dataaccess/deactivateLLMUsageTier";
import { fetchLLMUsageTiersByBase } from "../dataaccess/fetchLLMUsageTiersByBase";
import { useUser } from "../contexts/useUser";
import {
  LLMUsageTierStatus,
  LLMPeriod,
  LLMReplenishmentMethod,
  LLMUsageLimitAction,
} from "../API";

const schema = z.object({
  id: z.string().optional(),
  projectId: z.string().optional(),
  status: z.nativeEnum(LLMUsageTierStatus),
  name: z.string().min(1, "Name is required"),
  description: z.string().optional().nullable(),
  // Versioning & lineage (read-only in UI; provided by backend/flows)
  version: z.number().int().min(1).optional().nullable(),
  activeAt: z.string().optional().nullable(),
  inactiveAt: z.string().optional().nullable(),
  priorUsageTierId: z.string().optional().nullable(),
  baseUsageTierId: z.string().optional().nullable(),
  replenishmentPeriod: z.nativeEnum(LLMPeriod).optional().nullable(),
  replenishmentMethod: z
    .nativeEnum(LLMReplenishmentMethod)
    .optional()
    .nullable(),
  usageLimitAction: z.nativeEnum(LLMUsageLimitAction).optional().nullable(),
  standardTokenReplenishmentAmount: z
    .number()
    .int()
    .min(0)
    .optional()
    .nullable(),
  premiumTokenReplenishmentAmount: z
    .number()
    .int()
    .min(0)
    .optional()
    .nullable(),
  superPremiumTokenReplenishmentAmount: z
    .number()
    .int()
    .min(0)
    .optional()
    .nullable(),
  reasoningTokenReplenishmentAmount: z
    .number()
    .int()
    .min(0)
    .optional()
    .nullable(),
  standardCallReplenishmentAmount: z
    .number()
    .int()
    .min(0)
    .optional()
    .nullable(),
  premiumCallReplenishmentAmount: z.number().int().min(0).optional().nullable(),
  superPremiumCallReplenishmentAmount: z
    .number()
    .int()
    .min(0)
    .optional()
    .nullable(),
  reasoningCallReplenishmentAmount: z
    .number()
    .int()
    .min(0)
    .optional()
    .nullable(),

  // unlimited flags
  standardTokenUnlimited: z.boolean().optional().nullable(),
  premiumTokenUnlimited: z.boolean().optional().nullable(),
  superPremiumTokenUnlimited: z.boolean().optional().nullable(),
  reasoningTokenUnlimited: z.boolean().optional().nullable(),
  standardCallUnlimited: z.boolean().optional().nullable(),
  premiumCallUnlimited: z.boolean().optional().nullable(),
  superPremiumCallUnlimited: z.boolean().optional().nullable(),
  reasoningCallUnlimited: z.boolean().optional().nullable(),
});

type UsageTierFormType = z.infer<typeof schema>;

const UsageTierForm = () => {
  const { id } = useParams<{ id: string }>();
  const { project } = useUser();
  const navigate = useNavigate();
  const { logError } = useErrorLogging();

  const queryClient = useQueryClient();
  const mutationUpdateUsageTier = useMutation({
    mutationFn: updateLLMUsageTierAfterEdit,
    onSuccess: (result, vals) => {
      queryClient.invalidateQueries({ queryKey: ["usage-tiers", { id: id }] });

      window.history && window.history.length > 1
        ? navigate(-1)
        : navigate("/");
    },
    onError: (error) => {
      logError("Error updating usage tier", error as any);
    },
  });

  const mutationCreateUsageTier = useMutation({
    mutationFn: createLLMUsageTierAfterEdit,
    onSuccess: (result, vals) => {
      queryClient.invalidateQueries({ queryKey: ["usage-tiers"] });
      window.history && window.history.length > 1
        ? navigate(-1)
        : navigate("/");
    },
    onError: (error) => {
      logError("Error creating usage tier", error as any);
    },
  });

  const {
    data: usageTierData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["usage-tiers", { id: id }],
    queryFn: () =>
      API.graphql(graphqlOperation(getLLMUsageTier, { id: id })) as Promise<
        GraphQLResult<GetLLMUsageTierQuery>
      >,
    enabled: !!id,
  });

  // Check if in use (only for existing tiers)
  const { data: inUse } = useQuery({
    queryKey: ["usage-tier-inuse", { id }],
    queryFn: async () => {
      if (!id) return false;
      return isUsageTierInUse(id);
    },
    enabled: !!id,
    staleTime: 60_000,
  });

  // Load lineage (for display) if we have a tier
  const currentTier = usageTierData?.data?.getLLMUsageTier;
  const baseId = useMemo(() => {
    if (!currentTier) return undefined;
    return currentTier.baseUsageTierId ?? currentTier.id;
  }, [currentTier]);

  const { data: lineageTiers } = useQuery({
    queryKey: ["usage-tier-lineage", { baseId }],
    queryFn: async () => {
      if (!baseId) return [];
      return fetchLLMUsageTiersByBase(baseId);
    },
    enabled: !!baseId,
    staleTime: 60_000,
  });

  // Actions & read-only gating (hooks must be before any early return)
  const cloneMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("No tier id");
      const res = await cloneLLMUsageTier(id);
      const newId = (res as any)?.data?.createLLMUsageTier?.id as string | undefined;
      if (!newId) throw new Error("Clone failed: no id returned");
      return newId;
    },
    onSuccess: (newId) => {
      queryClient.invalidateQueries({ queryKey: ["usage-tiers"] });
      navigate(`/usage-tiers/edit/${newId}`);
    },
    onError: (e) => logError("Error cloning usage tier", e as any),
  });

  const activateMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("No tier id");
      return activateLLMUsageTier(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["usage-tiers", { id }] });
    },
    onError: (e) => logError("Error activating usage tier", e as any),
  });

  const deactivateMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("No tier id");
      return deactivateLLMUsageTier(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["usage-tiers", { id }] });
    },
    onError: (e) => logError("Error deactivating usage tier", e as any),
  });

  // Locale-aware date-time format: dd-MMM-yyyy hh:mm AM/PM using user's locale for month/AM-PM
  const formatDateTime = (iso?: string | null): string => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "-";
    const day = String(d.getDate()).padStart(2, "0");
    const month = d.toLocaleString(undefined, { month: "short" });
    const year = d.getFullYear();
    const time = d.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return `${day}-${month}-${year} ${time}`;
  };

  const { handleSubmit, control, reset, watch, getValues } =
    useForm<UsageTierFormType>({
      values: usageTierData?.data?.getLLMUsageTier
        ? {
            ...usageTierData.data.getLLMUsageTier,
          }
        : {
            projectId: project?.id,
            status: LLMUsageTierStatus.ACTIVE,
            name: "",
            description: "",
            replenishmentPeriod: LLMPeriod.MONTHLY,
            replenishmentMethod: LLMReplenishmentMethod.RESET,
            usageLimitAction: LLMUsageLimitAction.BLOCK,
            standardTokenReplenishmentAmount: 0,
            premiumTokenReplenishmentAmount: 0,
            superPremiumTokenReplenishmentAmount: 0,
            reasoningTokenReplenishmentAmount: 0,
            standardCallReplenishmentAmount: 500,
            premiumCallReplenishmentAmount: 250,
            superPremiumCallReplenishmentAmount: 0,
            reasoningCallReplenishmentAmount: 250,
            standardTokenUnlimited: true,
            premiumTokenUnlimited: true,
            superPremiumTokenUnlimited: true,
            reasoningTokenUnlimited: true,
            standardCallUnlimited: false,
            premiumCallUnlimited: false,
            superPremiumCallUnlimited: false,
            reasoningCallUnlimited: false,
          },
      resolver: zodResolver(schema),
    });

  if (id && isLoading) return <div>Loading...</div>;
  if (id && isError) return <div>Error loading usage tier</div>;

  const handleUpdate = (values: UsageTierFormType) => {
    if (!values) return;

    try {
      mutationUpdateUsageTier.mutate({ id: id, ...values } as any);
    } catch (error) {
      logError("Error updating usage tier", error as any);
    }
  };

  const handleCreate = (values: UsageTierFormType) => {
    if (!values) return;
    if (!project) return;

    mutationCreateUsageTier.mutate({
      projectId: project?.id,
      ...values,
    });
  };
  const handleClose = () => {
    window.history && window.history.length > 1 ? navigate(-1) : navigate("/");
  };

  const onSubmit = (data: UsageTierFormType) => {
    if (id) handleUpdate(data);
    else handleCreate(data);
  };

  // Actions & read-only gating
  const status = currentTier?.status as LLMUsageTierStatus | undefined;
  const readOnly = !!id && !!inUse; // read-only when in use

  return (
    <Paper
      elevation={3}
      style={{
        borderRadius: 3,
        border: "1px solid #444",
        margin: 2,
        padding: 2,
      }}
    >
      <Box padding={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {id
              ? `${currentTier?.name ?? "Usage Tier"} · v${currentTier?.version ?? "-"}`
              : "New Usage Tier"}
          </Typography>
          {id && (
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" onClick={() => cloneMutation.mutate()}>
                Clone New Version
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => activateMutation.mutate()}
                disabled={
                  status === LLMUsageTierStatus.ACTIVE ||
                  status === LLMUsageTierStatus.ARCHIVED ||
                  status === LLMUsageTierStatus.DELETED
                }
              >
                Activate
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={() => deactivateMutation.mutate()}
                disabled={status !== LLMUsageTierStatus.ACTIVE}
              >
                Deactivate
              </Button>
            </Stack>
          )}
        </Stack>
      </Box>

      {id && readOnly && (
        <Box px={2} pb={1}>
          <Alert severity="info">
            This usage tier is in use by customers and is now read-only. To edit, clone a new version.
          </Alert>
        </Box>
      )}

      {id && (
        <Box px={2} pb={2}>
          <Typography variant="subtitle2" gutterBottom>
            Lineage
          </Typography>
          <Paper variant="outlined" sx={{ p: 1 }}>
            <Typography variant="body2">
              Version: {currentTier?.version ?? "-"} | Status: {currentTier?.status}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Active At: {formatDateTime(currentTier?.activeAt)} | Inactive At: {formatDateTime(currentTier?.inactiveAt)}
            </Typography>
            {lineageTiers && (lineageTiers as any[]).length > 0 && (
              <Box mt={1}>
                <Divider sx={{ mb: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Versions in lineage: {(lineageTiers as any[]).map((t: any) => `v${t.version}(${t.status})`).join(", ")}
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      <Box component="fieldset" disabled={readOnly && !!id} sx={{ border: 0, m: 0, p: 0 }}>
        <Grid container padding={2} spacing={2}>
        <Grid item xs={12} md={6}>
          <TextFieldElement
            control={control}
            name="name"
            label="Name"
            size="small"
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SelectElement
            control={control}
            name="status"
            label="Status"
            size="small"
            required
            fullWidth
            options={[
              { id: LLMUsageTierStatus.DRAFT, label: "Draft" },
              { id: LLMUsageTierStatus.ACTIVE, label: "Active" },
              { id: LLMUsageTierStatus.INACTIVE, label: "Inactive" },
              { id: LLMUsageTierStatus.ARCHIVED, label: "Archived" },
              { id: LLMUsageTierStatus.DELETED, label: "Deleted" },
            ]}
          />
        </Grid>

        <Grid item xs={12}>
          <TextFieldElement
            control={control}
            name="description"
            label="Description"
            size="small"
            fullWidth
            multiline
            rows={2}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <SelectElement
            control={control}
            name="replenishmentPeriod"
            label="Replenishment Period"
            size="small"
            fullWidth
            options={[
              { id: LLMPeriod.NONE, label: "None" },
              { id: LLMPeriod.DAILY, label: "Daily" },
              { id: LLMPeriod.WEEKLY, label: "Weekly" },
              { id: LLMPeriod.MONTHLY, label: "Monthly" },
              { id: LLMPeriod.YEARLY, label: "Yearly" },
            ]}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SelectElement
            control={control}
            name="replenishmentMethod"
            label="Replenishment Method"
            size="small"
            fullWidth
            options={[
              { id: LLMReplenishmentMethod.NONE, label: "None" },
              { id: LLMReplenishmentMethod.RESET, label: "Reset" },
              { id: LLMReplenishmentMethod.ACCUMULATE, label: "Accumulate" },
            ]}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SelectElement
            control={control}
            name="usageLimitAction"
            label="Usage Limit Action"
            size="small"
            fullWidth
            options={[
              { id: LLMUsageLimitAction.BLOCK, label: "Block" },
              { id: LLMUsageLimitAction.ALLOW, label: "Allow" },
              { id: LLMUsageLimitAction.RESTRICT, label: "Restrict" },
            ]}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Call Replenishment Amounts
          </Typography>
        </Grid>

        <Grid item xs={12} md={6} sx={{ pr: { md: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ minWidth: '130px', whiteSpace: 'nowrap' }}>
              <CheckboxElement
                control={control}
                name="standardCallUnlimited"
                label="Unlimited, or "
              />
            </Box>
            <TextFieldElement
              control={control}
              name="standardCallReplenishmentAmount"
              label="Standard Call Replenishment"
              size="small"
              type="number"
              fullWidth
              disabled={watch("standardCallUnlimited") === true}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6} sx={{ pl: { md: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ minWidth: '130px', whiteSpace: 'nowrap' }}>
              <CheckboxElement
                control={control}
                name="premiumCallUnlimited"
                label="Unlimited, or "
              />
            </Box>
            <TextFieldElement
              control={control}
              name="premiumCallReplenishmentAmount"
              label="Premium Call Replenishment"
              size="small"
              type="number"
              fullWidth
              disabled={watch("premiumCallUnlimited") === true}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6} sx={{ pr: { md: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ minWidth: '130px', whiteSpace: 'nowrap' }}>
              <CheckboxElement
                control={control}
                name="superPremiumCallUnlimited"
                label="Unlimited, or "
              />
            </Box>
            <TextFieldElement
              control={control}
              name="superPremiumCallReplenishmentAmount"
              label="Super Premium Call Replenishment"
              size="small"
              type="number"
              fullWidth
              disabled={watch("superPremiumCallUnlimited") === true}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6} sx={{ pl: { md: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ minWidth: '130px', whiteSpace: 'nowrap' }}>
              <CheckboxElement
                control={control}
                name="reasoningCallUnlimited"
                label="Unlimited, or "
              />
            </Box>
            <TextFieldElement
              control={control}
              name="reasoningCallReplenishmentAmount"
              label="Reasoning Call Replenishment"
              size="small"
              type="number"
              fullWidth
              disabled={watch("reasoningCallUnlimited") === true}
            />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Token Replenishment Amounts
          </Typography>
        </Grid>

        <Grid item xs={12} md={6} sx={{ pr: { md: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ minWidth: '130px', whiteSpace: 'nowrap' }}>
              <CheckboxElement
                control={control}
                name="standardTokenUnlimited"
                label="Unlimited, or "
              />
            </Box>
            <TextFieldElement
              control={control}
              name="standardTokenReplenishmentAmount"
              label="Standard Token Replenishment"
              size="small"
              type="number"
              fullWidth
              disabled={watch("standardTokenUnlimited") === true}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6} sx={{ pl: { md: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ minWidth: '130px', whiteSpace: 'nowrap' }}>
              <CheckboxElement
                control={control}
                name="premiumTokenUnlimited"
                label="Unlimited, or "
              />
            </Box>
            <TextFieldElement
              control={control}
              name="premiumTokenReplenishmentAmount"
              label="Premium Token Replenishment"
              size="small"
              type="number"
              fullWidth
              disabled={watch("premiumTokenUnlimited") === true}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6} sx={{ pr: { md: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ minWidth: '130px', whiteSpace: 'nowrap' }}>
              <CheckboxElement
                control={control}
                name="superPremiumTokenUnlimited"
                label="Unlimited, or "
              />
            </Box>
            <TextFieldElement
              control={control}
              name="superPremiumTokenReplenishmentAmount"
              label="Super Premium Token Replenishment"
              size="small"
              type="number"
              fullWidth
              disabled={watch("superPremiumTokenUnlimited") === true}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6} sx={{ pl: { md: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ minWidth: '130px', whiteSpace: 'nowrap' }}>
              <CheckboxElement
                control={control}
                name="reasoningTokenUnlimited"
                label="Unlimited, or "
              />
            </Box>
            <TextFieldElement
              control={control}
              name="reasoningTokenReplenishmentAmount"
              label="Reasoning Token Replenishment"
              size="small"
              type="number"
              fullWidth
              disabled={watch("reasoningTokenUnlimited") === true}
            />
          </Box>
        </Grid>
        </Grid>
      </Box>
      <Box sx={{ padding: 2 }}>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant={"contained"}
          sx={{ marginRight: 2 }}
          disabled={readOnly && !!id}
        >
          Save and Close
        </Button>
        <Button onClick={() => reset()} variant={"outlined"} disabled={readOnly && !!id}>
          Reset
        </Button>
        <Button onClick={handleClose} variant={"outlined"}>
          Cancel
        </Button>
      </Box>
    </Paper>
  );
};

export default UsageTierForm;
